import React, { forwardRef, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Repeat } from 'lucide-react';
import { manageAutoRollLink } from '@/constants/autoRoll';
import { mergeRefs } from '@utils/mergeRefs';
import styles from './AutoRollBadge.module.css';

export interface IAutoRollBadgeProps extends Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  'href'
> {
  instructionId: string;
  /** Cadence text; shown as the hover hint and used as the accessible name. */
  label: string;
  paused?: boolean;
}

const AutoRollBadge = forwardRef<HTMLAnchorElement, IAutoRollBadgeProps>(
  ({ instructionId, label, paused = false, className, ...rest }, ref) => {
    const tooltipId = useId();
    const anchorRef = useRef<HTMLAnchorElement>(null);
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

    // Portalled with fixed coordinates: the badge sits in table cells that clip their overflow.
    const show = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (rect) setPosition({ top: rect.top + rect.height / 2, left: rect.right });
    };
    const hide = () => setPosition(null);

    return (
      <>
        <Link
          ref={mergeRefs(anchorRef, ref)}
          to={manageAutoRollLink(instructionId)}
          className={`${styles.badge} ${className ?? ''}`}
          data-paused={paused || undefined}
          aria-label={label}
          aria-describedby={position ? tooltipId : undefined}
          {...rest}
          onMouseEnter={show}
          onMouseLeave={hide}
          onFocus={show}
          onBlur={hide}
          // Badges sit inside clickable table rows; the row must not also navigate.
          onClick={(event) => event.stopPropagation()}
        >
          <Repeat size={14} strokeWidth={2} aria-hidden="true" />
        </Link>
        {position &&
          createPortal(
            <span
              id={tooltipId}
              role="tooltip"
              className={styles.tooltip}
              style={{ top: position.top, left: position.left }}
            >
              {label}
            </span>,
            document.body,
          )}
      </>
    );
  },
);

AutoRollBadge.displayName = 'AutoRollBadge';

export default AutoRollBadge;
